import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { PresenceUser } from "shared/hooks/presenceTypes";
import { AuthState } from "shared/reducers/authReducer";

import "./PresenceAvatars.css";
import { playJoinChime, playLeaveChime } from "./AudioChime";

const AVATAR_COLORS = [
  { bg: "#EEEDFE", text: "#3C3489" },
  { bg: "#E1F5EE", text: "#085041" },
  { bg: "#FAECE7", text: "#712B13" },
  { bg: "#FBEAF0", text: "#72243E" },
  { bg: "#E6F1FB", text: "#0C447C" },
  { bg: "#EAF3DE", text: "#27500A" },
];

const MAX_VISIBLE = 4;

interface PresenceAvatarsProps {
  users: PresenceUser[];
  getAction?: (resource: PresenceUser) => string | undefined;
}

const getInitials = (user: PresenceUser) =>
  `${user.first_name[0] ?? ""}${user.last_name[0] ?? ""}`.toUpperCase();

const getColor = (userId: number) =>
  AVATAR_COLORS[userId % AVATAR_COLORS.length];

const Avatar = ({
  user,
  index,
  action,
}: {
  user: PresenceUser;
  index: number;
  action?: string;
}) => {
  const [visible, setVisible] = useState(false);
  const color = getColor(user.id);
  const fullName = `${user.first_name} ${user.last_name}`;
  const tooltipText = action ? `${fullName} ${action}` : fullName;

  return (
    <div
      className="PresenceAvatar"
      style={{
        background: color.bg,
        color: color.text,
        zIndex: visible ? 100 : index,
      }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      aria-label={fullName}
      role="img"
    >
      {getInitials(user)}
      {visible && (
        <div className="PresenceAvatar__tooltip" role="tooltip">
          {tooltipText}
        </div>
      )}
    </div>
  );
};

export const PresenceAvatars = ({ users, getAction }: PresenceAvatarsProps) => {
  const currentUser = useSelector(
    ({ auth }: { auth: AuthState }) => auth?.user
  );
  const others = users.filter((u) => u.id !== currentUser?.id);
  const prevOthersRef = useRef<number>(0);

  useEffect(() => {
    const prev = prevOthersRef.current;
    const curr = others.length;
    if (curr > prev) playJoinChime();
    else if (curr < prev) playLeaveChime();
    prevOthersRef.current = curr;
  }, [others.length]);

  if (!others.length) return null;

  const visible = others.slice(0, MAX_VISIBLE);
  const overflow = others.length - MAX_VISIBLE;

  return (
    <div
      className="PresenceAvatars"
      role="group"
      aria-label={`${others.length} other user${
        others.length !== 1 ? "s" : ""
      } viewing this case`}
    >
      {visible.map((user, i) => (
        <Avatar
          key={user.id}
          user={user}
          index={i}
          action={getAction?.(user)}
        />
      ))}
      {overflow > 0 && (
        <div
          className="PresenceAvatars__overflow"
          aria-label={`${overflow} more users viewing`}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
};
